import { useState } from 'react';
import * as yup from 'yup';
import { FormikProvider, useFormik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import {
    Button,
    Checkbox,
    Dialog,
    Field,
    Input,
    MinMaxInputs,
    Switch,
    formikHelpers,
    toast,
} from '@round/creatorbase-ui';
import { outreachQueries } from '@round/api';
import { useCreateOutreachRequestMutation } from '@round/creatorbase-helpers/api-hooks';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { CreatorLocationField } from './components/CreatorLocationField/CreatorLocationField';
import { DeadlineField } from './components/DeadlineField/DeadlineField';
import { ErrorBanner } from './components/ErrorBanner/ErrorBanner';
import { HashtagTargets } from './components/HashtagTargets/HashtagTargets';
import { SoundTargets } from './components/SoundTargets/SoundTargets';
import { getDefaultRequestName, hasTarget, initialValues, mapToApi } from './helpers';
import { mapApiErrorsToForm } from './errors';
import { FormValues } from './types';
import styles from './CreateOutreachRequestModal.module.css';

const { Field: FormikField, InputField, CheckboxField } = formikHelpers;

const createUtils = outreachQueries.outreachRequests.utils.create;

const MAX_NAME_LENGTH = 255;

/** Always returned with a result, so they are shown as settled rather than offered as a choice. */
const LOCKED_OUTPUTS = ['Username', 'Link'];

const MAX_ENGAGEMENT_RATE_PERCENTAGE = 100;

/** Fine enough to ask for 2.5%, which is the sort of floor these requests are set at. */
const ENGAGEMENT_RATE_STEP = 0.1;

const OUTSIDE_TERRITORY_LABEL = 'Also include creators elsewhere';

const FORBIDDEN_MESSAGE = 'This client is not available to you. Reselect a client and try again.';
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * A max below the min is left alone, because the endpoint answers that one under
 * `filters.min_followers` and the error map already routes it.
 */
const countSchema = (label: string) =>
    yup.number().nullable().integer(`${label} must be a whole number`).min(0, `${label} cannot be negative`);

const validationSchema = yup.object({
    name: yup.string().trim().max(MAX_NAME_LENGTH, `Name must be ${MAX_NAME_LENGTH} characters or fewer`),
    minFollowers: countSchema('Followers'),
    maxFollowers: countSchema('Followers'),
    minAvgViews: countSchema('Average views'),
    minEngagementRate: yup
        .number()
        .nullable()
        .min(0, 'Engagement rate cannot be negative')
        .max(MAX_ENGAGEMENT_RATE_PERCENTAGE, `Engagement rate cannot be above ${MAX_ENGAGEMENT_RATE_PERCENTAGE}%`),
});

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

export const CreateOutreachRequestModal = ({ isOpen, onClose }: Props) => {
    const { selectedClientId } = useClientSelect();
    const queryClient = useQueryClient();
    const { create } = useCreateOutreachRequestMutation(queryClient);
    const [bannerMessage, setBannerMessage] = useState<string | null>(null);
    // A sound is only a target once it has resolved to a Music id, and Create waits for that.
    const [isResolvingTargets, setIsResolvingTargets] = useState(false);

    const formik = useFormik<FormValues>({
        initialValues,
        validationSchema,
        validateOnMount: true,
        onSubmit: (values, helpers) => {
            if (typeof selectedClientId !== 'number') {
                helpers.setSubmitting(false);
                return;
            }

            setBannerMessage(null);

            create(mapToApi(values, selectedClientId), {
                // The new request is read back off the list, so nothing is written into the cache
                // here — the mutation invalidates the outreach-request key and the list refetches.
                onSuccess: () => {
                    toast.info('Outreach request created');
                    helpers.resetForm();
                    onClose();
                },
                onError: (error) => {
                    helpers.setSubmitting(false);

                    if (!createUtils.isExpectedErrorResponse(error)) {
                        setBannerMessage(GENERIC_ERROR_MESSAGE);
                        return;
                    }

                    if (error.response.status === 403) {
                        setBannerMessage(FORBIDDEN_MESSAGE);
                        return;
                    }

                    const { errors, bannerMessage } = mapApiErrorsToForm(error.response.data);
                    helpers.setErrors(errors);
                    setBannerMessage(bannerMessage || null);
                },
            });
        },
    });

    const canSubmit = hasTarget(formik.values) && !isResolvingTargets && formik.isValid && !formik.isSubmitting;

    // Closing is silent — there is no discard confirmation — but a submit in flight owns the form
    // until it settles.
    const handleClose = () => {
        if (formik.isSubmitting) {
            return;
        }

        formik.resetForm();
        setBannerMessage(null);
        setIsResolvingTargets(false);
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <Dialog.Content className={styles.content}>
                <Dialog.Header>
                    <Dialog.Title>Create outreach request</Dialog.Title>
                    <Dialog.Description size="xs">Find TikTok creators by sound or hashtag.</Dialog.Description>
                </Dialog.Header>

                <FormikProvider value={formik}>
                    <Dialog.Body className={styles.body}>
                        {bannerMessage && <ErrorBanner message={bannerMessage} />}

                        <FormikField name="name">
                            <FormikField.Label htmlFor="name">Name</FormikField.Label>
                            {/* The placeholder is the name an empty field ends up with, so the fallback needs no copy. */}
                            <InputField
                                name="name"
                                placeholder={getDefaultRequestName()}
                                disabled={formik.isSubmitting}
                            />
                            <FormikField.Error name="name" />
                        </FormikField>

                        {/* The one place the "at least one target" rule is stated — Create only enforces it. */}
                        <Field.Set>
                            <Field.Legend variant="label">Targets</Field.Legend>
                            <Field.Description>
                                Add at least one TikTok sound or hashtag. Creators posting with them are what the
                                request searches for.
                            </Field.Description>

                            <SoundTargets disabled={formik.isSubmitting} onResolvingChange={setIsResolvingTargets} />
                            <HashtagTargets disabled={formik.isSubmitting} />
                        </Field.Set>

                        <Field.Set>
                            <Field.Legend variant="label">Outputs</Field.Legend>
                            <Field.Description>Columns returned alongside each creator.</Field.Description>

                            <div className={styles.outputs}>
                                {LOCKED_OUTPUTS.map((output) => (
                                    <Field key={output} orientation="horizontal">
                                        <Checkbox aria-label={output} checked disabled />
                                        <Field.Title>{output}</Field.Title>
                                    </Field>
                                ))}

                                <FormikField orientation="horizontal" name="outputEmail">
                                    <CheckboxField
                                        name="outputEmail"
                                        aria-label="Email"
                                        // Rate only comes back for creators with an email on file, so email
                                        // cannot be turned off while rate is asked for.
                                        disabled={formik.isSubmitting || formik.values.outputRate}
                                    />
                                    <FormikField.Content>
                                        <FormikField.Title>Email</FormikField.Title>
                                        <FormikField.Description>
                                            {formik.values.outputRate
                                                ? 'Required while rate is selected.'
                                                : 'Asking for an email returns only creators with one.'}
                                        </FormikField.Description>
                                    </FormikField.Content>
                                </FormikField>

                                <FormikField orientation="horizontal" name="outputLocation">
                                    <CheckboxField
                                        name="outputLocation"
                                        aria-label="Location"
                                        disabled={formik.isSubmitting}
                                    />
                                    <FormikField.Title>Location</FormikField.Title>
                                </FormikField>

                                <FormikField orientation="horizontal" name="outputRate">
                                    <CheckboxField
                                        name="outputRate"
                                        aria-label="Rate"
                                        disabled={formik.isSubmitting}
                                        // Rate only comes back for creators with an email on file, so asking
                                        // for it implies asking for email too.
                                        onCheckedChange={(checked: boolean) => {
                                            if (checked) {
                                                formik.setFieldValue('outputEmail', true);
                                            }
                                        }}
                                    />
                                    <FormikField.Content>
                                        <FormikField.Title>Rate</FormikField.Title>
                                        <FormikField.Description>
                                            We provide each creator&apos;s upfront asking rate.
                                        </FormikField.Description>
                                    </FormikField.Content>
                                </FormikField>
                            </div>
                        </Field.Set>

                        <Field.Set disabled={formik.isSubmitting}>
                            <Field.Legend variant="label">Filters</Field.Legend>
                            <Field.Description>
                                Narrow the creators a target returns. Anything left empty is not filtered on.
                            </Field.Description>

                            <CreatorLocationField />

                            {/* Loosening the match only means anything against a territory, so the toggle stays
                            out of the form until one is picked rather than sitting there disabled. */}
                            {formik.values.locations.length > 0 && (
                                <Field orientation="horizontal">
                                    <Switch
                                        size="sm"
                                        aria-label={OUTSIDE_TERRITORY_LABEL}
                                        checked={formik.values.locationMatch === 'loose'}
                                        onCheckedChange={(isLoose) =>
                                            formik.setFieldValue('locationMatch', isLoose ? 'loose' : 'strict')
                                        }
                                    />
                                    <Field.Title>{OUTSIDE_TERRITORY_LABEL}</Field.Title>
                                </Field>
                            )}

                            {/* Both bounds are optional and independent. A max below the min is left to the
                            endpoint, which answers under `filters.min_followers` — so the message lands on this
                            control either way. */}
                            <FormikField name="minFollowers">
                                <FormikField.Title>Followers</FormikField.Title>
                                <MinMaxInputs
                                    min={formik.values.minFollowers ?? undefined}
                                    max={formik.values.maxFollowers ?? undefined}
                                    onChange={(min, max) => {
                                        formik.setFieldValue('minFollowers', min ?? null);
                                        formik.setFieldValue('maxFollowers', max ?? null);
                                    }}
                                />

                                <FormikField.Error name="minFollowers" />
                                <FormikField.Error name="maxFollowers" />
                            </FormikField>

                            <div className={styles.metricFilters}>
                                {/* The rate is asked for the way it is read — a percentage — and converted to
                                the fraction the endpoint takes at submit time. */}
                                <FormikField name="minEngagementRate">
                                    <FormikField.Label htmlFor="minEngagementRate">
                                        Min engagement rate (%)
                                    </FormikField.Label>
                                    <Input
                                        id="minEngagementRate"
                                        type="number"
                                        placeholder="Any"
                                        min={0}
                                        max={MAX_ENGAGEMENT_RATE_PERCENTAGE}
                                        step={ENGAGEMENT_RATE_STEP}
                                        value={formik.values.minEngagementRate ?? ''}
                                        onChange={(event) =>
                                            formik.setFieldValue(
                                                'minEngagementRate',
                                                toNumberOrNull(event.target.value)
                                            )
                                        }
                                    />
                                    <FormikField.Error name="minEngagementRate" />
                                </FormikField>

                                <FormikField name="minAvgViews">
                                    <FormikField.Label htmlFor="minAvgViews">Min average views</FormikField.Label>
                                    <Input
                                        id="minAvgViews"
                                        type="number"
                                        placeholder="Any"
                                        min={0}
                                        step={1}
                                        value={formik.values.minAvgViews ?? ''}
                                        onChange={(event) =>
                                            formik.setFieldValue('minAvgViews', toNumberOrNull(event.target.value))
                                        }
                                    />
                                    <FormikField.Error name="minAvgViews" />
                                </FormikField>
                            </div>
                        </Field.Set>

                        <DeadlineField disabled={formik.isSubmitting} />
                    </Dialog.Body>
                </FormikProvider>

                <Dialog.Footer>
                    <Button variant="outline" size="sm" onClick={handleClose} disabled={formik.isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="default"
                        size="sm"
                        onClick={formik.submitForm}
                        disabled={!canSubmit}
                        isLoading={formik.isSubmitting}
                    >
                        Create request
                    </Button>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
};

/** A cleared filter is nothing set rather than zero, which is what the endpoint reads as no filter. */
const toNumberOrNull = (value: string) => {
    const parsed = Number(value);
    return value && !Number.isNaN(parsed) ? parsed : null;
};
